import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { SessionCheck, SignIn } from '../screens/SignIn';
import { FirstProfile } from '../screens/FirstProfile';
import { UNAUTHENTICATED_EVENT, checkSession, clearAuthNotice, consumeHadSession, markHadSession, parseAuthNotice, signOut as endSession, startGoogleSignIn, type AuthNotice, type ProfileDraft, type SessionUser } from '../services/session';
import { navigate } from '../lib/router';
import { isDemo } from '../lib/clock';
import { useApp } from '../state/store';

type Gate =
  | { kind: 'checking' }
  /** `at` re-mounts the sign-in screen so a repeated notice resets its button. */
  | { kind: 'signedOut'; notice: AuthNotice | null; loading?: boolean; at?: number }
  | { kind: 'profile'; user: SessionUser }
  | { kind: 'signedIn' };

interface AuthCtx { signOut: () => void }
const AuthContext = createContext<AuthCtx>({ signOut: () => undefined });
export const useAuth = (): AuthCtx => useContext(AuthContext);

const PREVIEW_STATES = ['default', 'loading', 'cancelled', 'expired', 'failed', 'suspended', 'checking', 'profile'] as const;
type PreviewState = (typeof PREVIEW_STATES)[number];

/** Demo-only: ?signin_preview=<state> renders one auth state for visual review. Ignored in api mode. */
function previewGate(): Gate | null {
  if (!isDemo || typeof window === 'undefined') return null;
  const v = new URLSearchParams(window.location.search).get('signin_preview') as PreviewState | null;
  if (!v || !PREVIEW_STATES.includes(v)) return null;
  if (v === 'checking') return { kind: 'checking' };
  if (v === 'profile') return { kind: 'profile', user: { name: 'Sofia Reyes' } };
  if (v === 'default') return { kind: 'signedOut', notice: null };
  if (v === 'loading') return { kind: 'signedOut', notice: null, loading: true };
  return { kind: 'signedOut', notice: v };
}

/** Session check → sign-in, first-login profile, or the app. The server is the authority. */
export function AuthGate({ children }: { children: ReactNode }) {
  const { act, clear } = useApp();
  const [preview] = useState(previewGate);
  const [gate, setGate] = useState<Gate>(preview ?? { kind: 'checking' });
  // Read the callback error once (an effect may run twice in development and clears it from the URL).
  const [returned] = useState(() => (typeof window === 'undefined' ? null : parseAuthNotice(window.location.search, window.location.hash)));

  const enter = useCallback((user: SessionUser) => {
    markHadSession();
    clearAuthNotice();
    setGate(user.needsProfile ? { kind: 'profile', user } : { kind: 'signedIn' });
  }, []);

  useEffect(() => {
    if (preview) return;
    let alive = true;
    clearAuthNotice();
    checkSession().then((r) => {
      if (!alive) return;
      if (r.status === 'signedIn') enter(r.user);
      else if (r.status === 'suspended') setGate({ kind: 'signedOut', notice: 'suspended' });
      else setGate({ kind: 'signedOut', notice: returned ?? (r.status === 'error' ? 'failed' : consumeHadSession() ? 'expired' : null) });
    });
    return () => {
      alive = false;
    };
  }, [preview, enter, returned]);

  // The data API reports 401 (session expired or revoked server-side) → back to sign-in.
  useEffect(() => {
    const onExpired = () => {
      clear();
      setGate((g) => (g.kind === 'signedIn' ? { kind: 'signedOut', notice: 'expired' } : g));
    };
    window.addEventListener(UNAUTHENTICATED_EVENT, onExpired);
    return () => window.removeEventListener(UNAUTHENTICATED_EVENT, onExpired);
  }, [clear]);

  const onContinue = useCallback(() => {
    clearAuthNotice();
    const r = startGoogleSignIn();
    if (r === 'unconfigured') setGate({ kind: 'signedOut', notice: 'unconfigured', at: Date.now() });
    if (r === 'demo') {
      // Demo only: brief pause so the loading state is visible, then re-run the session check.
      setTimeout(() => checkSession().then((s) => s.status === 'signedIn' && enter(s.user)), 700);
    }
  }, [enter]);

  const onProfile = useCallback((d: ProfileDraft | null) => {
    if (d && isDemo) {
      void act.saveProfile({ bio: d.bio || undefined, affiliations: { list: d.affiliations, primary: d.primary } });
      act.setPrivacy('publicProfile', d.publicProfile);
    }
    // Keep the deep link the person arrived with (api mode: the returnTo route); otherwise Home.
    setGate({ kind: 'signedIn' });
  }, [act]);

  const signOut = useCallback(() => {
    void endSession().finally(() => {
      navigate({ name: 'home' }, { replace: true });
      clear(); // api mode: drop the previous member's data from memory
      setGate({ kind: 'signedOut', notice: null, at: Date.now() });
    });
  }, [clear]);
  const ctx = useMemo(() => ({ signOut }), [signOut]);

  if (gate.kind === 'checking') return <SessionCheck />;
  if (gate.kind === 'signedOut') return <SignIn key={`${gate.notice ?? 'none'}-${gate.at ?? 0}`} notice={gate.notice} forceLoading={gate.loading} onContinue={preview ? () => undefined : onContinue} />;
  if (gate.kind === 'profile') return <FirstProfile initialName={gate.user.name ?? ''} onDone={preview ? () => undefined : onProfile} />;
  return <AuthContext.Provider value={ctx}><div className="fade" style={{ height: '100%' }}>{children}</div></AuthContext.Provider>;
}
